import { useMutation, useQueryClient } from "@tanstack/react-query";

import { processRequest } from "@api/config";
import { TPostFilter, modelCamPostsAPI } from "@api/modelCamPosts";

import { useAuthQuery } from "@lib/queryClient";

import { API_PREFIXES, QUERY_KEYS } from "@configs/api";

type PostListQueryParams = {
	filter?: TPostFilter;
	page?: number;
	limit?: number;
	offset?: number;
};

export const usePostListQuery = ({ filter = "publish", page = 1, limit = 10, offset = 0 }: PostListQueryParams = {}) =>
	useAuthQuery({
		queryKey: [QUERY_KEYS.POST_LIST, filter, page, limit, offset],
		queryFn: async () => {
			const data = await processRequest({
				prefix: API_PREFIXES.GET_POST_LIST,
				request: (token) => modelCamPostsAPI.getPostListAsync({ token, page, limit, offset, filter }),
			});

			return data;
		},
	});

export const useCreatePostMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (formData: FormData) =>
			processRequest({
				prefix: API_PREFIXES.CREATE_POST,
				request: (token) => modelCamPostsAPI.createPostAsync({ token, formData }),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POST_LIST] });
		},
	});
};

export const useUpdatePostMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ postId, formData }: { postId: string; formData: FormData }) =>
			processRequest({
				prefix: API_PREFIXES.UPDATE_POST,
				request: (token) => modelCamPostsAPI.updatePostAsync({ token, postId, formData }),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POST_LIST] });
		},
	});
};

export const useDeletePostMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (postId: string) =>
			processRequest({
				prefix: API_PREFIXES.DELETE_POST,
				request: (token) => modelCamPostsAPI.deletePostAsync({ token, postId }),
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.POST_LIST] });
		},
	});
};
